import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface PageHeaderProps {
  title: string;
  /**
   * Small Atmosphere section chip rendered above the title, e.g.
   * "Financials / Transactions". Uses the light stop as the fill and the
   * dark stop for text so it matches the sidebar's wayfinding accent.
   * This is the one colored element in the header — the title stays Humus.
   */
  eyebrow?: ReactNode;
  /** Short record metadata under the title (SKU, amount, email). Not for prose. */
  meta?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  eyebrow,
  meta,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        'flex flex-col gap-2 pb-6 sm:flex-row sm:items-end sm:justify-between',
        className,
      )}
    >
      <div className="space-y-1">
        {eyebrow && (
          <div className="pb-0.5">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-info-soft/60 bg-info-soft/30 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide text-info">
              <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-info" />
              {eyebrow}
            </span>
          </div>
        )}
        <h1 className="text-2xl font-medium tracking-headline leading-headline text-foreground">
          {title}
        </h1>
        {meta && <div className="text-sm text-muted-foreground">{meta}</div>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
  );
}
